// FILE: src/services/caseStudyService.js
//
// Client wrapper for the Case Study Question Framework RPCs (migration 071).
// Design: docs/case-study-framework-proposal.md (Rev 3).
//
// Every learner-facing payload from these RPCs is answer-stripped: correct
// answers, rationales, and author notes only ever come back from
// submitCaseAnswer (practice mode) or getCaseReview (completed attempts).
// Base-table access is admin-only under RLS, so this module is the ONLY
// learner path to case content.

import { supabase } from './supabaseClient';

async function callRpc(fn, args) {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) {
    throw new Error(`[caseStudyService] ${fn}: ${error.message}`);
  }
  return data;
}

// ---------------------------------------------------------------------------
// Learner
// ---------------------------------------------------------------------------

/** Published cases for a certification, with the caller's attempt status. */
export function listCaseStudies(certification) {
  return callRpc('get_case_studies_learner', { p_certification: certification });
}

/**
 * Intro payload (no live attempt) or stage-scoped resume payload (in_progress
 * attempt). Resume state is recomputed server-side from responses.
 */
export function getCaseRuntime(caseStudyId) {
  return callRpc('get_case_runtime', { p_case_study_id: caseStudyId });
}

/** Starts an attempt; returns the attempt plus the stage-1 payload. */
export function startCaseAttempt(caseStudyId, { mode = 'practice', source = 'user' } = {}) {
  return callRpc('start_case_attempt', {
    p_case_study_id: caseStudyId,
    p_mode: mode,
    p_source: source,
  });
}

/**
 * Submits one answer. The server verifies ownership, case-version membership,
 * and active-stage ordering, then scores and stores. Returns stage/case
 * progression (and, in practice mode, correctness + rationale).
 */
export function submitCaseAnswer(attemptId, caseSequence, answer, {
  flagged = false,
  timeSpentSeconds = null,
  firstAnswerSeconds = null,
  answerChangeCount = 0,
} = {}) {
  return callRpc('submit_case_answer', {
    p_attempt_id: attemptId,
    p_case_sequence: caseSequence,
    p_answer: answer,
    p_flagged: flagged,
    p_time_spent_seconds: timeSpentSeconds,
    p_first_answer_seconds: firstAnswerSeconds,
    p_answer_change_count: answerChangeCount,
  });
}

/** Full review payload — completed attempts only (enforced server-side). */
export function getCaseReview(attemptId) {
  return callRpc('get_case_review', { p_attempt_id: attemptId });
}

export function abandonCaseAttempt(attemptId) {
  return callRpc('abandon_case_attempt', { p_attempt_id: attemptId });
}

// ---------------------------------------------------------------------------
// Admin (RPCs gate internally via _caller_admin_email)
// ---------------------------------------------------------------------------

export function adminListCaseStudies(certification = null) {
  return callRpc('admin_list_case_studies', { p_certification: certification });
}

export function adminGetCaseFull(caseStudyId) {
  return callRpc('admin_get_case_full', { p_case_study_id: caseStudyId });
}

export function adminUpsertCaseStudy(payload) {
  return callRpc('admin_upsert_case_study', { p_payload: payload });
}

export function adminUpsertCaseStage(payload) {
  return callRpc('admin_upsert_case_stage', { p_payload: payload });
}

export function adminUpsertCaseSection(payload) {
  return callRpc('admin_upsert_case_section', { p_payload: payload });
}

export function adminUpsertCaseArtifact(payload) {
  return callRpc('admin_upsert_case_artifact', { p_payload: payload });
}

export function adminLinkCaseQuestion(questionId, caseStudyId, stageId, caseSequence) {
  return callRpc('admin_link_case_question', {
    p_question_id: questionId,
    p_case_study_id: caseStudyId,
    p_stage_id: stageId,
    p_case_sequence: caseSequence,
  });
}

export function adminUnlinkCaseQuestion(questionId) {
  return callRpc('admin_unlink_case_question', { p_question_id: questionId });
}

export function adminSetQuestionArtifacts(questionId, refs) {
  return callRpc('admin_set_question_artifacts', {
    p_question_id: questionId,
    p_refs: refs,
  });
}

export function validateCaseStudy(caseStudyId) {
  return callRpc('validate_case_study', { p_case_study_id: caseStudyId });
}

export function publishCaseStudy(caseStudyId) {
  return callRpc('publish_case_study', { p_case_study_id: caseStudyId });
}

// ---------------------------------------------------------------------------
// Admin editing (migration 072) — atomic reorders + guarded deletes
// ---------------------------------------------------------------------------

/**
 * Atomically apply a full question layout. `order` must cover every linked
 * question exactly once: [{question_id, stage_id, case_sequence}, ...].
 * The server two-phases the update so unique indexes never see duplicates.
 */
export function adminReorderCaseQuestions(caseStudyId, order) {
  return callRpc('admin_reorder_case_questions', {
    p_case_study_id: caseStudyId,
    p_order: order,
  });
}

/** Atomically renumber stages: [{stage_id, sequence}, ...] covering all. */
export function adminReorderCaseStages(caseStudyId, order) {
  return callRpc('admin_reorder_case_stages', {
    p_case_study_id: caseStudyId,
    p_order: order,
  });
}

/** Refuses (server-side) while questions are still linked to the stage. */
export function adminDeleteCaseStage(stageId) {
  return callRpc('admin_delete_case_stage', { p_stage_id: stageId });
}

export function adminDeleteCaseSection(sectionId) {
  return callRpc('admin_delete_case_section', { p_section_id: sectionId });
}

/** Refuses while questions still reference the artifact. */
export function adminDeleteCaseArtifact(artifactId) {
  return callRpc('admin_delete_case_artifact', { p_artifact_id: artifactId });
}

/** Drafts only; refuses when learner attempts exist. */
export function adminDeleteCaseStudy(caseStudyId) {
  return callRpc('admin_delete_case_study', { p_case_study_id: caseStudyId });
}

// ---------------------------------------------------------------------------
// Case bundles (migration 074) — whole-case import/export
// ---------------------------------------------------------------------------

/**
 * Atomically import a pm-pixel-case-bundle@1. mode 'create' refuses existing
 * slugs; 'replace' rebuilds draft/review cases in place (published refuses).
 * Any error rolls the entire import back server-side.
 */
export function adminImportCaseStudy(bundle, mode = 'create') {
  return callRpc('admin_import_case_study', { p_bundle: bundle, p_mode: mode });
}

/** Export a case as a self-contained bundle (same shape the importer takes). */
export function adminExportCaseStudy(caseStudyId) {
  return callRpc('admin_export_case_study', { p_case_study_id: caseStudyId });
}
